import { z } from "zod";
import {
  EcommerceItemSourceCartSchema,
  EcommerceItemSourceItemsSchema,
} from "./get-ecommerce-data";
import {
  AddPaymentInfoEventSchema,
  AddShippingInfoEventSchema,
  AddToCartEventSchema,
  BeginCheckoutEventSchema,
  PurchaseEventSchema,
  RemoveFromCartEventSchema,
  SelectItemEventSchema,
  SelectPromotionEventSchema,
  ViewCartEventSchema,
  ViewEcommerceItemEventSchema,
  ViewEcommerceItemListEventSchema,
  ViewPromotionEventSchema,
} from "./events-schemas";

const ViewEcommerceItemEventInputSchema = z.object({
  eventName: ViewEcommerceItemEventSchema.shape.eventName,
  skus: EcommerceItemSourceItemsSchema.shape.skus,
});

const ViewEcommerceItemListEventInputSchema = z.object({
  eventName: ViewEcommerceItemListEventSchema.shape.eventName,
  item_list_id:
    ViewEcommerceItemListEventSchema.shape.ecommerce.shape.item_list_id,
  item_list_name:
    ViewEcommerceItemListEventSchema.shape.ecommerce.shape.item_list_name,
  skus: EcommerceItemSourceItemsSchema.shape.skus,
});

const SelectItemEventInputSchema = z.object({
  eventName: SelectItemEventSchema.shape.eventName,
  item_list_id: SelectItemEventSchema.shape.ecommerce.shape.item_list_id,
  item_list_name: SelectItemEventSchema.shape.ecommerce.shape.item_list_name,
  index: z.number(),
  skus: EcommerceItemSourceItemsSchema.shape.skus,
});

const SelectPromotionEventInputSchema = z.object({
  eventName: SelectPromotionEventSchema.shape.eventName,
  promotion_id: SelectPromotionEventSchema.shape.ecommerce.shape.promotion_id,
  promotion_name:
    SelectPromotionEventSchema.shape.ecommerce.shape.promotion_name,
  creative_name: SelectPromotionEventSchema.shape.ecommerce.shape.creative_name,
  creative_slot: SelectPromotionEventSchema.shape.ecommerce.shape.creative_slot,
  skus: EcommerceItemSourceItemsSchema.shape.skus,
});

const ViewPromotionEventInputSchema = z.object({
  eventName: ViewPromotionEventSchema.shape.eventName,
  promotion_id: ViewPromotionEventSchema.shape.ecommerce.shape.promotion_id,
  promotion_name: ViewPromotionEventSchema.shape.ecommerce.shape.promotion_name,
  creative_name: ViewPromotionEventSchema.shape.ecommerce.shape.creative_name,
  creative_slot: ViewPromotionEventSchema.shape.ecommerce.shape.creative_slot,
  skus: EcommerceItemSourceItemsSchema.shape.skus,
});

const AddToCartEventInputSchema = z.object({
  eventName: AddToCartEventSchema.shape.eventName,
  skus: EcommerceItemSourceItemsSchema.shape.skus,
  cartId: z.string().optional(),
  coupon: z.string().optional(),
  quantity: z.number().positive().optional(),
});

const RemoveFromCartEventInputSchema = z.object({
  eventName: RemoveFromCartEventSchema.shape.eventName,
  skus: EcommerceItemSourceItemsSchema.shape.skus,
  cartId: z.string().optional(),
  coupon: z.string().optional(),
  quantity: z.number().positive().optional(),
});

const ViewCartEventInputSchema = z.object({
  eventName: ViewCartEventSchema.shape.eventName,
  cartId: EcommerceItemSourceCartSchema.shape.cartId,
});

const BeginCheckoutEventInputSchema = z.object({
  eventName: BeginCheckoutEventSchema.shape.eventName,
  cartId: EcommerceItemSourceCartSchema.shape.cartId,
});

const AddShippingInfoEventInputSchema = z.object({
  eventName: AddShippingInfoEventSchema.shape.eventName,
  shipping_tier:
    AddShippingInfoEventSchema.shape.ecommerce.shape.shipping_tier.optional(),
  cartId: EcommerceItemSourceCartSchema.shape.cartId,
});

const AddPaymentInfoEventInputSchema = z.object({
  eventName: AddPaymentInfoEventSchema.shape.eventName,
  payment_type:
    AddPaymentInfoEventSchema.shape.ecommerce.shape.payment_type.optional(),
  cartId: EcommerceItemSourceCartSchema.shape.cartId,
});

const PurchaseEventInputSchema = z.object({
  eventName: PurchaseEventSchema.shape.eventName,
  cartId: EcommerceItemSourceCartSchema.shape.cartId,
});

const SubscriptionBeginCheckoutEventInputSchema = z.object({
  eventName: z.literal("subscription_begin_checkout"),
  sku: z.string(),
});

const SubscriptionAddShippingInfoEventInputSchema = z.object({
  eventName: z.literal("subscription_add_shipping_info"),
  sku: z.string(),
  shipping_tier:
    AddShippingInfoEventSchema.shape.ecommerce.shape.shipping_tier.optional(),
});

const SubscriptionAddPaymentInfoEventInputSchema = z.object({
  eventName: z.literal("subscription_add_payment_info"),
  sku: z.string(),
  payment_type:
    AddPaymentInfoEventSchema.shape.ecommerce.shape.payment_type.optional(),
});

const SubscriptionPurchaseEventInputSchema = z.object({
  eventName: z.literal("subscription_purchase"),
  sku: z.string(),
  contractId: z.string(),
});

export const EcommerceEventParamsSchema = z.discriminatedUnion("eventName", [
  AddPaymentInfoEventInputSchema,
  AddShippingInfoEventInputSchema,
  AddToCartEventInputSchema,
  BeginCheckoutEventInputSchema,
  PurchaseEventInputSchema,
  RemoveFromCartEventInputSchema,
  SelectItemEventInputSchema,
  SelectPromotionEventInputSchema,
  ViewCartEventInputSchema,
  ViewEcommerceItemEventInputSchema,
  ViewEcommerceItemListEventInputSchema,
  ViewPromotionEventInputSchema,
  SubscriptionAddPaymentInfoEventInputSchema,
  SubscriptionPurchaseEventInputSchema,
  SubscriptionAddShippingInfoEventInputSchema,
  SubscriptionBeginCheckoutEventInputSchema,
]);

export type EcommerceEventParams = z.infer<typeof EcommerceEventParamsSchema>;

export function EcommerceEvent(event: EcommerceEventParams) {
  return EcommerceEventParamsSchema.parse(event);
}
